type RGB = { r: number; g: number; b: number };

function hexToRgb(hex: string): RGB | null {
  const clean = hex.replace('#', '');
  if (![3, 6].includes(clean.length)) return null;
  const normalized = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean;
  const intVal = Number.parseInt(normalized, 16);
  if (Number.isNaN(intVal)) return null;
  return {
    r: (intVal >> 16) & 255,
    g: (intVal >> 8) & 255,
    b: intVal & 255,
  };
}

function parseRgb(input: string): RGB | null {
  const match = input.match(/rgba?\((\d{1,3}),\s*(\d{1,3}),\s*(\d{1,3})/i);
  if (!match) return null;
  return {
    r: Number.parseInt(match[1], 10),
    g: Number.parseInt(match[2], 10),
    b: Number.parseInt(match[3], 10),
  };
}

function extractColors(value: string): RGB[] {
  const colors: RGB[] = [];
  const tokens = value.match(/#(?:[0-9a-f]{3}){1,2}\b|rgba?\([^)]*\)/gi);
  if (!tokens) return colors;
  tokens.forEach((token) => {
    const rgb = token.startsWith('#') ? hexToRgb(token) : parseRgb(token);
    if (rgb) colors.push(rgb);
  });
  return colors;
}

function relativeLuminance({ r, g, b }: RGB) {
  const srgb = [r, g, b].map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * srgb[0] + 0.7152 * srgb[1] + 0.0722 * srgb[2];
}

export function contrastRatio(colorA: string, colorB: string) {
  const a = pickRgb(colorA);
  const b = pickRgb(colorB);
  if (!a || !b) return 1;
  const lum1 = relativeLuminance(a) + 0.05;
  const lum2 = relativeLuminance(b) + 0.05;
  return lum1 > lum2 ? lum1 / lum2 : lum2 / lum1;
}

function pickRgb(value: string): RGB | null {
  if (!value) return null;
  const rgb = value.startsWith('#') ? hexToRgb(value) : parseRgb(value);
  if (rgb) return rgb;
  const gradientColors = extractColors(value);
  if (gradientColors.length > 0) {
    // average gradient stops for a reasonable contrast guess
    const avg = gradientColors.reduce(
      (acc, c) => ({ r: acc.r + c.r, g: acc.g + c.g, b: acc.b + c.b }),
      { r: 0, g: 0, b: 0 },
    );
    const len = gradientColors.length;
    return { r: Math.round(avg.r / len), g: Math.round(avg.g / len), b: Math.round(avg.b / len) };
  }
  return null;
}

/**
 * Decide whether to use light or dark text on a given background.
 * Supports solid colors and CSS gradients.
 */
export function pickTextColorForBackground(
  background: string,
  { light = '#FFFFFF', dark = '#1A1A1A', threshold = 4.5 } = {},
) {
  const bgRgb = pickRgb(background);
  if (!bgRgb) return light;

  const whiteContrast = contrastRatioFromRgb(bgRgb, hexToRgb(light)!);
  const blackContrast = contrastRatioFromRgb(bgRgb, hexToRgb(dark)!);

  if (whiteContrast >= threshold || whiteContrast > blackContrast) return light;
  return dark;
}

function contrastRatioFromRgb(a: RGB, b: RGB) {
  const lum1 = relativeLuminance(a) + 0.05;
  const lum2 = relativeLuminance(b) + 0.05;
  return lum1 > lum2 ? lum1 / lum2 : lum2 / lum1;
}

export function getGradientStops(gradient: string): string[] {
  const tokens = gradient.match(/#(?:[0-9a-f]{3}){1,2}\b|rgba?\([^)]*\)/gi);
  return tokens ?? [];
}

function adjustChannel(value: number, amount: number) {
  return Math.max(0, Math.min(255, value + amount));
}

export function lighten(color: string, amount = 10) {
  const rgb = pickRgb(color);
  if (!rgb) return color;
  return `rgb(${adjustChannel(rgb.r, amount)}, ${adjustChannel(rgb.g, amount)}, ${adjustChannel(
    rgb.b,
    amount,
  )})`;
}

export function darken(color: string, amount = 10) {
  return lighten(color, -amount);
}

export function mix(colorA: string, colorB: string, ratio = 0.5) {
  const a = pickRgb(colorA);
  const b = pickRgb(colorB);
  if (!a || !b) return colorA;
  const mixChannel = (c1: number, c2: number) => Math.round(c1 * (1 - ratio) + c2 * ratio);
  return `rgb(${mixChannel(a.r, b.r)}, ${mixChannel(a.g, b.g)}, ${mixChannel(a.b, b.b)})`;
}

/**
 * Get a contrasting color for text on a background that's not pure black or white.
 * Uses a lighter or darker shade based on the background luminance, providing
 * better visual contrast than pure black/white while maintaining readability.
 *
 * @param background - Background color (hex, rgb, or gradient)
 * @param options - Optional customization
 * @returns A color string with good contrast to the background
 */
export function getContrastingTextColor(
  background: string,
  options: {
    lightIntensity?: number;
    darkIntensity?: number;
  } = {}
) {
  const {
    lightIntensity = 0.9,
    darkIntensity = 0.1,
  } = options;

  const bgRgb = pickRgb(background);
  if (!bgRgb) return 'rgba(255, 255, 255, 0.95)';

  const luminance = relativeLuminance(bgRgb);

  // For dark backgrounds (low luminance), use light text
  if (luminance < 0.5) {
    // Create a lighter shade by mixing background with white
    const lightened = {
      r: Math.round(bgRgb.r + (255 - bgRgb.r) * lightIntensity),
      g: Math.round(bgRgb.g + (255 - bgRgb.g) * lightIntensity),
      b: Math.round(bgRgb.b + (255 - bgRgb.b) * lightIntensity),
    };
    return `rgba(${lightened.r}, ${lightened.g}, ${lightened.b}, 0.95)`;
  }

  // For light backgrounds (high luminance), use dark text
  const darkened = {
    r: Math.round(bgRgb.r * darkIntensity),
    g: Math.round(bgRgb.g * darkIntensity),
    b: Math.round(bgRgb.b * darkIntensity),
  };
  return `rgba(${darkened.r}, ${darkened.g}, ${darkened.b}, 0.85)`;
}

/**
 * Badge styling for an arbitrary lookup/brand colour (course, status, need, leader type, …).
 *
 * The idiom used across the app — `{ backgroundColor: `${c}22`, color: c }` — paints the label in the
 * raw brand colour over a 13%-alpha wash of that same hue. When the brand colour is dark and the theme
 * is dark (or the colour is pale on a light theme) the label drops below readable contrast and the badge
 * looks blank. Filling the badge with the colour and picking the label by luminance keeps it legible in
 * both themes, whatever the colour happens to be.
 *
 * Falls back to neutral theme tokens when the colour is missing or not a valid hex.
 */
export function brandBadgeStyle(color?: string | null): { backgroundColor: string; color: string } {
  const hex = (color ?? '').trim();

  if (!hex || !hexToRgb(hex)) {
    return {
      backgroundColor: 'var(--mantine-color-default-border)',
      color: 'var(--mantine-color-text)',
    };
  }

  return { backgroundColor: hex, color: pickTextColorForBackground(hex) };
}

/**
 * A transparent wash of `color`, safe for **both** hex values and CSS custom properties.
 *
 * ⚠️ The `` `${color}22` `` idiom that appears throughout the app only works for bare hex. Given a
 * theme token it produces `var(--mantine-color-blue-6)22`, which is not a colour at all — the browser
 * discards the declaration and the element renders with no background. Silent, and easy to miss on a
 * surface that already looks close to the intended tint.
 *
 * Mirrors Mantine's own `alpha()`: `color-mix` for custom properties, `rgba()` for hex.
 *
 * Use this for **decoration only** — a selected-card wash, a hover state. Never put text or an icon in
 * the same hue on top of it; use `brandSolidStyle` for that.
 */
export function brandTint(color: string | null | undefined, alpha = 0.14): string {
  const value = (color ?? '').trim();
  if (!value) return 'transparent';

  if (value.startsWith('var(')) {
    return `color-mix(in srgb, ${value}, transparent ${Math.round((1 - alpha) * 100)}%)`;
  }

  const rgb = hexToRgb(value);
  if (!rgb) return 'transparent';

  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
}

/**
 * Filled styling for an icon chip / avatar carrying a lookup colour — the `brandBadgeStyle` treatment
 * for something that isn't a `Badge`.
 *
 * The usual `{ backgroundColor: `${c}22`, color: c }` puts the glyph in the raw brand colour over a 13%
 * wash of the same hue. On the dark theme a dark brand colour then sits on a dark card and the icon
 * disappears; on the light theme a pale one does the same. Filling with the colour and picking the glyph
 * by relative luminance keeps it readable whatever the lookup happens to be set to.
 *
 * Accepts CSS custom properties too, in which case the glyph falls back to the theme's contrast token —
 * luminance can't be computed from a variable at runtime without reading the cascade.
 */
export function brandSolidStyle(color?: string | null): { backgroundColor: string; color: string } {
  const value = (color ?? '').trim();

  if (!value) {
    return {
      backgroundColor: 'var(--mantine-color-default-border)',
      color: 'var(--mantine-color-text)',
    };
  }

  if (value.startsWith('var(')) {
    return { backgroundColor: value, color: 'var(--mantine-color-white)' };
  }

  if (!hexToRgb(value)) {
    return {
      backgroundColor: 'var(--mantine-color-default-border)',
      color: 'var(--mantine-color-text)',
    };
  }

  return { backgroundColor: value, color: pickTextColorForBackground(value) };
}
